import { createServerStateStore } from "./serverState";

export type DeliveryStatus =
  | "Hazır"
  | "Taşıma Halinde"
  | "Teslim Edildi";

export interface DeliveryAssignment {
  shareId: number;
  status: DeliveryStatus;
  animalId?: number;
  customerId?: number;
  customerName?: string;
  address?: string;
  carrierId?: number;
  carrierUserId?: number;
  carrierName?: string;
  carrierPhone?: string;
  shareNo?: string;
  id?: string | number;
  note?: string;
  liveLocationUrl?: string;
  startedAt?: string;
  deliveredAt?: string;
  liveLocation?: {
    lat: number;
    lng: number;
    updatedAt?: string;
    accuracy?: number;
  };
  [key: string]: any;
}

function normalizeStatus(value?: string): DeliveryStatus {
  const raw = String(value ?? "");

  if (raw === "Taşıma Halinde") return "Taşıma Halinde";
  if (raw === "Teslim Edildi") return "Teslim Edildi";

  return "Hazır";
}

function normalizeRecord(item: any): DeliveryAssignment {
  return {
    ...item,
    shareId: Number(item?.shareId ?? 0),
    status: normalizeStatus(item?.status),
    customerName:
      item?.customerName === undefined ? undefined : String(item.customerName),
    address: item?.address === undefined ? undefined : String(item.address),
    carrierName:
      item?.carrierName === undefined ? undefined : String(item.carrierName),
    carrierPhone:
      item?.carrierPhone === undefined ? undefined : String(item.carrierPhone),
  };
}

function normalizeRecords(value: unknown): DeliveryAssignment[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => normalizeRecord(item))
    .filter((item) => Number.isFinite(item.shareId) && item.shareId > 0);
}

const store = createServerStateStore<DeliveryAssignment[]>(
  "delivery_assignments",
  [],
  normalizeRecords
);

export function getDeliveryAssignments() {
  return store.getState();
}

export async function rehydrateDeliveryAssignments() {
  return store.rehydrate();
}

export async function setAllDeliveryAssignments(
  updater:
    | DeliveryAssignment[]
    | ((prev: DeliveryAssignment[]) => DeliveryAssignment[])
) {
  return store.setState((prev) =>
    normalizeRecords(typeof updater === "function" ? updater(prev) : updater)
  );
}

export function subscribeDeliveryAssignments(listener: () => void) {
  return store.subscribe(listener);
}

export async function resetDeliveryAssignmentsStore() {
  return store.reset();
}

export async function upsertDeliveryAssignment(item: DeliveryAssignment) {
  const nextItem = normalizeRecord(item);

  return setAllDeliveryAssignments((prev) => {
    const next = [...prev];
    const index = next.findIndex((row) => row.shareId === nextItem.shareId);

    if (index >= 0) {
      next[index] = {
        ...next[index],
        ...nextItem,
      };
    } else {
      next.push(nextItem);
    }

    return next;
  });
}

export async function removeDeliveryAssignment(
  deliveryIdOrShareId: string | number
) {
  return setAllDeliveryAssignments((prev) =>
    prev.filter((item) => {
      const byShareId = item.shareId === Number(deliveryIdOrShareId);
      const byId =
        item.id !== undefined &&
        String(item.id) === String(deliveryIdOrShareId);

      return !byShareId && !byId;
    })
  );
}

export async function setDeliveryStatus(
  shareId: number,
  status: DeliveryStatus
) {
  return setAllDeliveryAssignments((prev) =>
    prev.map((item) =>
      item.shareId === shareId ? { ...item, status } : item
    )
  );
}

export async function updateDeliveryStatus(
  deliveryIdOrShareId: string | number,
  status: DeliveryStatus
) {
  return setAllDeliveryAssignments((prev) =>
    prev.map((item) => {
      const byShareId = item.shareId === Number(deliveryIdOrShareId);
      const byId =
        item.id !== undefined &&
        String(item.id) === String(deliveryIdOrShareId);

      return byShareId || byId ? { ...item, status } : item;
    })
  );
}

export async function updateCarrierLiveLocation(
  carrierIdOrShareId: number,
  latOrLocation:
    | number
    | string
    | {
        lat: number;
        lng: number;
        accuracy?: number;
      },
  lng?: number,
  accuracy?: number
) {
  if (typeof latOrLocation === "string") {
    return setAllDeliveryAssignments((prev) =>
      prev.map((item) =>
        item.carrierId === carrierIdOrShareId ||
        item.carrierUserId === carrierIdOrShareId
          ? {
              ...item,
              liveLocationUrl: latOrLocation,
            }
          : item
      )
    );
  }

  if (typeof latOrLocation === "object") {
    const location = latOrLocation;

    return setAllDeliveryAssignments((prev) =>
      prev.map((item) =>
        item.carrierId === carrierIdOrShareId ||
        item.carrierUserId === carrierIdOrShareId
          ? {
              ...item,
              liveLocation: {
                lat: location.lat,
                lng: location.lng,
                accuracy: location.accuracy,
                updatedAt: new Date().toISOString(),
              },
            }
          : item
      )
    );
  }

  return setAllDeliveryAssignments((prev) =>
    prev.map((item) =>
      item.shareId === carrierIdOrShareId
        ? {
            ...item,
            liveLocation: {
              lat: latOrLocation,
              lng: Number(lng ?? 0),
              accuracy,
              updatedAt: new Date().toISOString(),
            },
          }
        : item
    )
  );
}

export async function cleanupDeliveryAssignments(
  sharesOrIds?: number[] | Array<{ id: number }>
) {
  if (!Array.isArray(sharesOrIds) || sharesOrIds.length === 0) {
    return store.getState();
  }

  const validSet = new Set(
    sharesOrIds
      .map((item) =>
        typeof item === "number" ? item : Number(item?.id ?? 0)
      )
      .filter((id) => Number.isFinite(id) && id > 0)
  );

  return setAllDeliveryAssignments((prev) =>
    prev.filter((item) => validSet.has(item.shareId))
  );
}